import { useRef, useState, type FormEvent } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogTrigger,
} from "./ui/dialog";

type FeedbackStep = "form" | "success";

interface FeedbackButtonProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

const FeedbackButton = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
}: FeedbackButtonProps = {}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const [step, setStep] = useState<FeedbackStep>("form");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [allowContact, setAllowContact] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const titleInputRef = useRef<HTMLInputElement>(null);
  const okButtonRef = useRef<HTMLButtonElement>(null);

  function resetForm() {
    setTitle("");
    setDescription("");
    setEmail("");
    setAllowContact(false);
    setErrorMessage("");
  }

  function handleOpenChange(nextOpen: boolean) {
    if (isControlled) {
      controlledOnOpenChange?.(nextOpen);
    } else {
      setUncontrolledOpen(nextOpen);
    }
    if (!nextOpen) {
      window.setTimeout(() => {
        setStep("form");
        resetForm();
      }, 200);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage("");
    setIsSubmitting(true);

    const feedback = {
      title: title.trim(),
      description: description.trim(),
      email: email.trim(),
      allowContact,
    };

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(feedback),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "Failed to submit feedback.");
      }

      setStep("success");
    } catch (error) {
      console.error("Feedback submission error:", error);
      setErrorMessage(
        error instanceof Error ? error.message : "Unable to submit feedback.",
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {!hideTrigger && (
        <DialogTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="text-primary-foreground feedbackBtn"
          >
            Feedback
          </Button>
        </DialogTrigger>
      )}

      <DialogContent
        className="sm:max-w-lg"
        onEscapeKeyDown={(e) => isSubmitting && e.preventDefault()}
        onPointerDownOutside={(e) => isSubmitting && e.preventDefault()}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          if (step === "form") titleInputRef.current?.focus();
          else okButtonRef.current?.focus();
        }}
      >
        {step === "form" ? (
          <>
            <DialogHeader>
              <DialogTitle>Share Your Feedback</DialogTitle>
              <DialogDescription>
                We value your feedback. Please let us know your suggestions or
                report any issues you have encountered.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <p className="text-sm text-muted-foreground">
                Fields marked with an asterisk (*) are required.
              </p>

              {errorMessage !== "" && (
                <div
                  role="alert"
                  aria-live="assertive"
                  className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
                >
                  {errorMessage}
                </div>
              )}

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="feedback-title">
                  Title{" "}
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                </Label>
                <Input
                  ref={titleInputRef}
                  id="feedback-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isSubmitting}
                  maxLength={200}
                  aria-required="true"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="feedback-description">
                  Description{" "}
                  <span aria-hidden="true" className="text-destructive">
                    *
                  </span>
                </Label>
                <Textarea
                  id="feedback-description"
                  rows={5}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isSubmitting}
                  maxLength={4000}
                  aria-required="true"
                  required
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="feedback-email">Email</Label>
                <Input
                  id="feedback-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isSubmitting}
                  maxLength={255}
                  autoComplete="email"
                />
              </div>

              <div className="flex items-start gap-2">
                <Checkbox
                  id="feedback-allow-contact"
                  checked={allowContact}
                  onCheckedChange={(checked) =>
                    setAllowContact(checked === true)
                  }
                  disabled={isSubmitting}
                  className="mt-0.5"
                />
                <Label htmlFor="feedback-allow-contact" className="font-normal">
                  I allow you to contact me regarding this feedback.
                </Label>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting…" : "Submit"}
                </Button>
                <span
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  className="sr-only"
                >
                  {isSubmitting
                    ? "Your feedback is being submitted. Please wait."
                    : ""}
                </span>
              </DialogFooter>
            </form>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Feedback Submitted</DialogTitle>
              <DialogDescription>
                Thank you. Your feedback was submitted successfully.
              </DialogDescription>
            </DialogHeader>

            <div className="flex flex-col items-center gap-4 py-6 text-center">
              <div
                aria-hidden="true"
                className="flex size-14 items-center justify-center rounded-full bg-primary text-2xl font-bold text-primary-foreground"
              >
                &#10003;
              </div>
            </div>

            <DialogFooter>
              <Button
                ref={okButtonRef}
                type="button"
                onClick={() => handleOpenChange(false)}
              >
                OK
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackButton;
